import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { REVENUE_KEYS } from 'constants/analytics';
import { OrganizationListRow } from 'types/api/organizations';
import {
  fetchAnalyticsMonthly,
  fetchAnalyticsReview,
  fetchAnalyticsUtmValues
} from 'services/api/analytics';

import {
  DynamicItem,
  DynamicResponse,
  FetchAnalyticsParams,
  FetchUtmValuesParams,
  IAnalyticsState,
  RevenueData,
  UtmValuesResponse
} from './types';
import {
  analyticsReviewCache,
  analyticsDynamicsCache,
  analyticsUtmValuesCache
} from 'utils/cacheWithQuery';

export const fetchAnalyticsData = createAsyncThunk(
  'patients/fetchAnalyticsData',
  async (params: FetchAnalyticsParams, { dispatch }) => {
    const searchParams = new URLSearchParams();

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value) {
          searchParams.append(key, String(value));
        }
      });
    }

    const query = searchParams.toString();

    dispatch(
      fetchUtmValues({ startDate: params.startDate, endDate: params.endDate })
    );

    const cachedData = analyticsReviewCache.getData(query);

    if (cachedData) {
      return {
        analytics: cachedData.analytics,
        revenueAndQuantityByItem: cachedData.revenueAndQuantityByItem
      };
    }

    const data = await fetchAnalyticsReview(query);

    analyticsReviewCache.setData(query, data);

    return {
      analytics: data.analytics,
      revenueAndQuantityByItem: data.revenueAndQuantityByItem
    };
  }
);

export const fetchDynamicsData = createAsyncThunk<
  DynamicResponse[],
  FetchAnalyticsParams
>('patients/fetchDynamicsData', async (params, { dispatch }) => {
  const searchParams = new URLSearchParams();

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value) {
        searchParams.append(key, String(value));
      }
    });
  }

  const query = searchParams.toString();

  dispatch(
    fetchUtmValues({ startDate: params.startDate, endDate: params.endDate })
  );

  const cachedData = analyticsDynamicsCache.getData(query);

  if (cachedData) {
    return cachedData;
  }

  const data = await fetchAnalyticsMonthly(query);

  analyticsDynamicsCache.setData(query, data);

  return data;
});

export const fetchUtmValues = createAsyncThunk<
  UtmValuesResponse,
  FetchUtmValuesParams
>('patients/fetchUtmValues', async params => {
  const searchParams = new URLSearchParams();

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value) {
        searchParams.append(key, String(value));
      }
    });
  }

  const query = searchParams.toString();

  const cachedData = analyticsUtmValuesCache.getData(query);

  if (cachedData) {
    return cachedData;
  }

  const data = await fetchAnalyticsUtmValues(query);

  analyticsUtmValuesCache.setData(query, data);

  return data;
});

const initialState: IAnalyticsState = {
  analytics: {
    totalUsers: '0',
    conversionRate: '0',
    revenue: '0',
    totalOrders: '0'
  },
  revenuePrice: {
    subscription_monthly: 0,
    subscription_yearly: 0,
    video_consultation: 0,
    radiology: 0
  },
  revenueQuantity: {
    users: 0,
    total_orders: 0,
    subscription_monthly: 0,
    subscription_yearly: 0,
    video_consultation: 0,
    radiology: 0
  },
  dynamics: [],
  utmValues: {
    loading: false,
    data: {
      utm_mediums: [],
      utm_campaigns: [],
      utm_terms: []
    }
  },
  loading: false,
  commission: true,
  organizationsCommissions: {}
};

const analyticsSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    toggleCommission: (state, action: PayloadAction<boolean>) => {
      state.commission = action.payload;
    },
    addOrganizationsInfo: (
      state,
      action: PayloadAction<OrganizationListRow[]>
    ) => {
      state.organizationsCommissions = action.payload.reduce<
        IAnalyticsState['organizationsCommissions']
      >((acc, val) => {
        if (val.id) {
          const commission = (val.commission ?? {}) as Record<string, unknown>;
          acc[val.id] = {
            subscription_monthly: Number(commission.subscription_monthly) || 0,
            subscription_yearly: Number(commission.subscription_yearly) || 0,
            video_consultation: Number(commission.video_consultation) || 0,
            radiology: Number(commission.radiology) || 0
          };
        }

        return acc;
      }, {});
    }
  },
  extraReducers: builder => {
    builder.addCase(fetchAnalyticsData.pending, state => {
      state.loading = true;
    });
    builder.addCase(fetchAnalyticsData.fulfilled, (state, action) => {
      const { analytics } = action.payload;

      state.loading = false;
      state.analytics = {
        ...action.payload.analytics,
        conversionRate:
          analytics.totalUsers != 0
            ? analytics.totalOrders / analytics.totalUsers
            : '0'
      };

      state.revenueQuantity.users = Number(action.payload.analytics.totalUsers);
      state.revenueQuantity.total_orders = Number(
        action.payload.analytics.totalOrders
      );

      REVENUE_KEYS.forEach(key => {
        const item = action.payload.revenueAndQuantityByItem.find(
          (data: DynamicItem) => data.itemName === key
        );

        state.revenuePrice[key as keyof RevenueData] = item?.revenue || 0;
        state.revenueQuantity[key as keyof RevenueData] = item?.quantity || 0;
      });
    });
    builder.addCase(fetchAnalyticsData.rejected, state => {
      state.loading = false;
      state.analytics = {
        ...initialState.analytics
      };
      state.revenuePrice = {
        ...initialState.revenuePrice
      };
      state.revenueQuantity = {
        ...initialState.revenueQuantity
      };
    });
    builder.addCase(fetchDynamicsData.pending, state => {
      state.loading = true;
    });
    builder.addCase(fetchDynamicsData.fulfilled, (state, action) => {
      state.loading = false;

      state.dynamics = action.payload.map(item => {
        const rest = REVENUE_KEYS.reduce((acc, val) => {
          const result = item.items.find(data => data.itemName === val);

          if (result) {
            return {
              ...acc,
              [`${val}_quantity`]: result.quantity,
              [`${val}_price`]: result.revenue
            };
          }

          return acc;
        }, {});

        return {
          month: item.month,
          revenue: item.revenue,
          totalUsers: item.totalUsers,
          transactions: item.transactions,
          conversionRate:
            item.totalUsers != 0 ? item.transactions / item.totalUsers : 0,
          subscription_monthly_quantity: 0,
          subscription_monthly_price: 0,
          subscription_yearly_quantity: 0,
          subscription_yearly_price: 0,
          video_consultation_quantity: 0,
          video_consultation_price: 0,
          radiology_quantity: 0,
          radiology_price: 0,
          ...rest
        };
      });
    });
    builder.addCase(fetchDynamicsData.rejected, state => {
      state.loading = false;
      state.dynamics = [];
    });
    builder.addCase(fetchUtmValues.pending, state => {
      state.utmValues.loading = true;
    });
    builder.addCase(fetchUtmValues.fulfilled, (state, action) => {
      state.utmValues.data = action.payload;
      state.utmValues.loading = false;
    });
    builder.addCase(fetchUtmValues.rejected, state => {
      state.utmValues = {
        loading: false,
        data: {
          ...state.utmValues.data
        }
      };
    });
  }
});

export const { toggleCommission, addOrganizationsInfo } =
  analyticsSlice.actions;
export default analyticsSlice.reducer;
